<template>
  <TemplateLandingDetail v-if="page" :page="page" />
</template>

<script setup lang="ts">
import { getIndirectClientHeaders, seoHead } from '~/assets/seo'
import { getSiteConfig } from '~/composables/craft'
import { useSeoRedirect } from '~/composables/seo-redirect'
import { setCacheControlHeader, setPageCacheControlHeader } from '~/composables/util'
import { useNavigationStore } from '~/store/navigation'
import { useGlobalStore } from '~/store/global'
import { usePageStore } from '~/store/page'
import { ignoreShelterSiteHandles } from '~/store/shelter'

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    nl: '/www/[departmentTypeSlug]/[siteSlug]',
  },
})

const route = useRoute()
const navigationStore = useNavigationStore()
const globalStore = useGlobalStore()
const pageStore = usePageStore()
const config = useRuntimeConfig()
const seoRedirect = useSeoRedirect()
let page = null

const { site, siteSlug } = getSiteConfig(route)

if (siteSlug === undefined) {
  setCacheControlHeader(600, true)
  const redirect = await seoRedirect(site, route.fullPath)
  if (redirect === false) {
    throw createError({ statusCode: 404, message: 'Page not found' })
  }
} else if (route.path.endsWith('/') && false !== (await seoRedirect(site, route.fullPath))) {
  /**
   * [DB-748] This route also matches a site root WITH a trailing slash (`/dierenasiel/zuidwolde/`),
   * which would serve the same landing page as the canonical URL without the slash
   * (`/dierenasiel/zuidwolde`). The landing page is always fetched with the fixed URI `__home__`
   * (see below), so the CMS never sees the requested path and cannot canonicalize it on its own.
   * Therefore, for a path with a trailing slash, first ask the CMS through `seoRedirect`: when the
   * CMS answers with a redirect (a 301 to the canonical URL), `seoRedirect` has already applied it
   * via `navigateTo`, and this branch only sets a cache header on that redirect response. When the
   * CMS does not redirect (`false`), the landing page is fetched and served as usual below.
   */
  setCacheControlHeader(600, true)
} else {
  const pageStoreFetchUriOptions = {
    headers: getIndirectClientHeaders(),
    notFoundStrategy: config.environment !== 'dev' && ignoreShelterSiteHandles.includes(site) ? 'ignore' : undefined,
  }
  const [, , seoGlobal, page_] = await Promise.all([
    navigationStore.fetch(site),
    globalStore.fetch({ site, push: true }),
    globalStore.fetchSeo(),
    pageStore.fetchUri({ site, uri: `__home__`, push: true, options: pageStoreFetchUriOptions }),
  ])
  page = page_

  if (!page) {
    setCacheControlHeader(600, true)
    const redirect = await seoRedirect(site, route.fullPath)
    if (redirect === false) {
      throw createError({ statusCode: 404, message: 'Page not found', fatal: true })
    }
  } else {
    setPageCacheControlHeader(page)

    useHead(() => seoHead(page, seoGlobal))
  }
}
</script>
